import React, { Component, Fragment } from 'react';
import gql from 'graphql-tag';
import moment from 'moment';
import { compose, Query } from '../../reactApolloCompat';
import { withRouter } from '../../utils/withRouter';
import cloneDeep from 'lodash.clonedeep';
import { withErrorBoundary } from '@sentry/react';
import withStyles from '@mui/styles/withStyles';
import Hidden from '../../components/Hidden';
import Grid from '../../components/layout/Grid';
import Paper from '../../components/layout/Paper';
import Inline, { inlineAlignment } from '../../components/layout/Inline';
import CremationRunsheet from './CremationRunsheet';
import PlaqueRunsheet from './PlaqueRunsheet';
import LinearProgressIndicator from '../../components/loading/LinearProgressIndicator';
import DispatchDate from '../../fragments/DispatchDate';
import SpecialInstruction from '../../fragments/SpecialInstruction';
import ReflectionRoom from '../../fragments/ReflectionRoom';
import Deceased from '../../fragments/Deceased';
import Member from '../../fragments/Member';
import DatePickerField from '../../components/form/DatePickerField';
import { GQL_REFETCH_60S, subscribe } from '../../utils/subscriptions';
import PrintContent from '../../components/PrintContent';
import Button from '../../components/form/Button';
import ErrorFallback from '../../components/ErrorFallback';
import { flattenConnection } from '../../utils/objects';
import { getDeliveryDateTime, getInsertionDateTime } from '../../utils/date';
import { DriverArrivalsIcon, FireIcon, PlaqueSignIcon, PrintIcon } from '../../components/IconIndex';
import { numberToWords } from '../../utils/strings';
import Checkbox from '../../components/form/Checkbox';

class Runsheet extends Component {
    constructor(props) {
        super(props);

        const saved = JSON.parse(sessionStorage.getItem('runsheet')) || {};
        let beginDate = saved.date || moment().format('YYYYMMDD');
        if (!moment(beginDate).isBetween(moment().subtract(100, 'years'), moment().add(100, 'years')))
            beginDate = moment().format('YYYYMMDD');
        let untilDate = saved.until || beginDate;
        if (!moment(untilDate).isBetween(moment(beginDate), moment().add(100, 'years')))
            untilDate = moment().format('YYYYMMDD');

        this.state = {
            dateFrom: moment(beginDate).format('YYYY-MM-DD'),
            dateTo: moment(untilDate).format('YYYY-MM-DD')
        };

        this.runsheetQueryRef = React.createRef();
        subscribe(this.runsheetQueryRef, GQL_REFETCH_60S);
        this.runsheetQuery2Ref = React.createRef();
        subscribe(this.runsheetQuery2Ref, GQL_REFETCH_60S);
        this.storePolling = null;
    }

    render() {
        return (
            <Fragment>
                {this.renderHeader()}
                <Grid container>
                    <Grid item>
                        <div className="runsheet-paper" ref={e => (this.formRef = e)}>
                            {this.renderPageContent()}
                        </div>
                    </Grid>
                </Grid>
            </Fragment>
        );
    }

    renderHeader() {
        const { dateFrom, dateTo } = this.state;
        const saved = JSON.parse(sessionStorage.getItem('runsheet')) || {};
        const preferences = [
            {
                label: 'Hide insertions',
                checked: saved.hideInsertions || false,
                onChange: (evt, hideInsertions) => this.updateSession({ newPreferences: { hideInsertions } })
            },
            {
                label: 'Hide arrivals',
                checked: saved.hideArrivals || false,
                onChange: (evt, hideArrivals) => this.updateSession({ newPreferences: { hideArrivals } })
            },
            {
                label: 'Hide installations',
                checked: saved.hidePlaques || false,
                onChange: (evt, hidePlaques) => this.updateSession({ newPreferences: { hidePlaques } })
            },
            {
                label: 'Hide completed',
                checked: saved.hideCompleted || false,
                onChange: (evt, hideCompleted) => this.updateSession({ newPreferences: { hideCompleted } })
            }
        ];

        return (
            <Grid container mr={10}>
                <Grid item xs={12} sm={12} md={3}>
                    <Inline alignment={inlineAlignment.rightAlignSiblings} center>
                        <h1 className="title">Run Sheet</h1>
                        <Hidden mdUp>
                            <PrintContent
                                pageStyle={
                                    '@media print { body { -webkit-print-color-adjust: exact; color-adjust: exact; } }'
                                }
                                trigger={() => (
                                    <Button variant="primary icon-button">
                                        <PrintIcon />
                                    </Button>
                                )}
                                content={() => this.formRef}
                            />
                        </Hidden>
                    </Inline>
                </Grid>
                <Grid item xs={6} md={3}>
                    <DatePickerField label="From" type="date" value={dateFrom} name="dateFrom"
                                     onChange={this.dateChange} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <DatePickerField label="Until" type="date" value={dateTo} name="dateTo"
                                     onChange={this.dateChange} />
                </Grid>
                <Hidden smDown>
                    <Grid item xs={12} sm={12} md={3}>
                        <Inline className="button-alignment" alignment={inlineAlignment.right}>
                            <PrintContent
                                pageStyle={
                                    '@media print { body { -webkit-print-color-adjust: exact; color-adjust: exact; } }'
                                }
                                trigger={() => (
                                    <Button variant="primary">
                                        <PrintIcon />
                                        <Hidden smDown>Print</Hidden>
                                    </Button>
                                )}
                                content={() => this.formRef}
                            />
                        </Inline>
                    </Grid>
                </Hidden>
                <Grid item>
                    <Inline center style={{ justifyContent: 'center', margin: -12, zoom: 0.88 }}>
                        {preferences.map(pref => (
                            <Checkbox key={pref.label} checked={pref.checked} label={pref.label}
                                      onChange={pref.onChange} />
                        ))}
                    </Inline>
                </Grid>
            </Grid>
        );
    }

    dateChange = e => {
        const { dateFrom, dateTo } = this.state;
        const diff = moment(dateTo).diff(dateFrom, 'days');
        // enforce a maximum of 7 days in the runsheet.
        const newDates = { dateFrom, dateTo };

        if (e.target.name === 'dateFrom') {
            if (moment(e.target.value).isAfter(dateTo)) {
                newDates.dateFrom = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateTo = moment(e.target.value)
                    .add(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (moment(e.target.value).isBefore(moment(dateTo).subtract(7, 'days'))) {
                newDates.dateFrom = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateTo = moment(e.target.value)
                    .add(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (!!e.target.value) {
                newDates.dateFrom = e.target.value;
            }
        } else {
            if (moment(e.target.value).isBefore(dateFrom)) {
                newDates.dateTo = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateFrom = moment(e.target.value)
                    .subtract(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (moment(e.target.value).isAfter(moment(dateFrom).add(7, 'days'))) {
                newDates.dateTo = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateFrom = moment(e.target.value)
                    .subtract(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (!!e.target.value) {
                newDates.dateTo = e.target.value;
            }
        }
        this.updateSession({ newDates });

        this.setState(newDates, () => this.restartPolling());
    };

    renderPageContent() {
        const { dateFrom, dateTo } = this.state;
        const variables = {
            filterBy: [{ field: 'CremationCancelled', value: '0' }],
            fromDate: dateFrom, toDate: dateTo
        };
        return (
            <Fragment>
                <Query
                    query={cremationsQuery}
                    fetchPolicy="cache-and-network"
                    variables={variables}
                    ref={this.runsheetQueryRef}
                >
                    {response1 => {
                        if (response1.loading && response1.networkStatus === 1) {
                            return (
                                <Fragment>
                                    <div>Loading data, please wait a moment...</div>
                                    <LinearProgressIndicator />
                                </Fragment>
                            );
                        }

                        return (
                            <Query
                                query={plaquesQuery}
                                fetchPolicy="cache-and-network"
                                variables={{ fromDate: dateFrom, toDate: dateTo }}
                                ref={this.runsheetQuery2Ref}
                            >
                                {response2 => {
                                    if (response2.loading && response2.networkStatus === 1) {
                                        return (
                                            <Fragment>
                                                <div>Loading data, please wait a moment...</div>
                                                <LinearProgressIndicator />
                                            </Fragment>
                                        );
                                    }

                                    const data = { ...response1.data, ...response2.data };
                                    this.storePolling = {
                                        start: [response1.startPolling, response2.startPolling],
                                        stop: [response1.stopPolling, response2.stopPolling]
                                    };
                                    return this.renderDays(data, response2.networkStatus);
                                }}
                            </Query>
                        );
                    }}
                </Query>
            </Fragment>
        );
    }

    renderDays(data, networkStatus) {
        const { dateFrom, dateTo } = this.state;
        const getDays = () => {
            let arr = {};
            let dt = dateFrom;
            while (moment(dt) <= moment(dateTo)) {
                arr[moment(dt).format('YYYY-MM-DD')] = [];
                dt = moment(dt).add(1, 'd');
            }
            return arr;
        };
        let daylist = getDays();
        daylist = this.addUniqueDaysToDays(data, daylist);

        let days = [];
        for (let item in daylist) {
            days.push(item);
        }

        const saved = JSON.parse(sessionStorage.getItem('runsheet')) || {};
        const { hideInsertions, hideArrivals, hidePlaques, hideCompleted } = saved;

        return (
            <Fragment>
                {!!networkStatus && networkStatus !== 7 && (
                    <Fragment>
                        <div>Loading data, please wait a moment...</div>
                        <LinearProgressIndicator />
                    </Fragment>
                )}
                {days.map((dayItem, i) => {
                    const hasArrivingCremOrders = !!daylist[days[i]] && daylist[days[i]].ArrivingCremOrders;
                    const hasInsertingCremations = !!daylist[days[i]] && daylist[days[i]].InsertingCremOrders;
                    const hasReadyPlaqueOrders = !!daylist[days[i]] && daylist[days[i]].ReadyPlaqueOrders;

                    return (
                        <Paper elevation={0} key={i} className={i ? 'page-break' : null}>
                            <Grid container>
                                <Grid item>
                                    <h2>Daily Run Sheet for {moment(dayItem).format('dddd[,] LL')}</h2>

                                    <p className="no-actions">
                                        <FireIcon />
                                        {hasInsertingCremations && 1 === hasInsertingCremations.length ? <>
                                            There is </> : <>
                                            There are </>}<span>{hasInsertingCremations
                                        ? numberToWords(hasInsertingCremations.length)
                                        : 'no'} cremation insertions</span> on this day.

                                    </p>
                                    <p className="no-actions">
                                        <DriverArrivalsIcon /> There are <span>{hasArrivingCremOrders
                                        ? numberToWords(hasArrivingCremOrders.length)
                                        : 'no'} cremation arrivals</span> on this day.
                                    </p>
                                    <p className="no-actions">
                                        <PlaqueSignIcon /> There are <span>{hasReadyPlaqueOrders
                                        ? numberToWords(hasReadyPlaqueOrders.length)
                                        : 'no'} plaque installations</span> on this day.
                                    </p>

                                    {hasInsertingCremations && !hideInsertions &&
                                        this.renderInsertionTable(daylist[days[i]].InsertingCremOrders, hideCompleted)
                                    }
                                    {hasArrivingCremOrders && !hideArrivals &&
                                        this.renderArrivalTable(daylist[days[i]].ArrivingCremOrders, hideCompleted)
                                    }
                                    {hasReadyPlaqueOrders && !hidePlaques &&
                                        this.renderPlaqueTable(daylist[days[i]].ReadyPlaqueOrders, hideCompleted)
                                    }
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}
            </Fragment>
        );
    }

    addUniqueDaysToDays(data, daylist) {
        let ArrivingCremOrders = [].concat(
            this.FilterByDate(data, 'arrivingCremOrders', ['DeliveryDate', 'EstimatedDeliveryDate', 'CremationDate'], true)
        );

        let InsertingCremOrders = [].concat(
            this.FilterByDate(data, 'insertingCremOrders', ['InsertionDate', 'EstimatedInsertionDate', 'CremationEndDate', 'CremationDate'], true)
        );

        let ReadyPlaqueOrders = [].concat(
            this.FilterByDate(data, 'readyPlaqueOrders', ['InstallationDate'])
        );

        //cycle through appointments and create unique days
        return this.getUniqueDates(
            daylist,
            ArrivingCremOrders.sort((a, b) => moment(getDeliveryDateTime(a)) - moment(getDeliveryDateTime(b))),
            InsertingCremOrders.sort((a, b) => moment(getInsertionDateTime(a)) - moment(getInsertionDateTime(b))),
            ReadyPlaqueOrders.sort((a, b) => moment(a.InstallationDate) - moment(b.InstallationDate)),
            'Date'
        );
    }

    FilterByDate(original, propertyName, dateObjs) {
        const { dateFrom, dateTo } = this.state;
        const myCopy = cloneDeep(original);
        flattenConnection(myCopy, propertyName);
        let datesTimesAdded = [];

        !!myCopy && !!myCopy[propertyName] && myCopy[propertyName].forEach(function(item) {
            dateObjs.every(dateObj => {
                if (!item[dateObj]) return true;
                let date = moment(item[dateObj]);
                if (date.isBetween(dateFrom, dateTo, 'day', '[]')) {
                    item['Date'] = date.format('YYYY-MM-DD');
                    item['Time'] = date.format('HH:mm:ss');
                    item['Type'] = propertyName;
                    datesTimesAdded.push(item);
                    return false;
                }
                return true;
            });
        });

        return datesTimesAdded;
    }

    getUniqueDates(days, ArrivingCremOrders, InsertingCremOrders, ReadyPlaqueOrders, key) {
        ArrivingCremOrders.forEach(function(item) {
            if (!(item[key] in days)) {
                days[item[key]] = [];
            }
            let tempDays = days[item[key]];
            if (!tempDays.ArrivingCremOrders) {
                tempDays.ArrivingCremOrders = [];
                tempDays.ArrivingCremOrders.push(item);
            } else {
                tempDays.ArrivingCremOrders.push(item);
            }
        });

        InsertingCremOrders.forEach(function(item) {
            if (!(item[key] in days)) {
                days[item[key]] = [];
            }
            let tempDays = days[item[key]];
            if (!tempDays.InsertingCremOrders) {
                tempDays.InsertingCremOrders = [];
                tempDays.InsertingCremOrders.push(item);
            } else {
                tempDays.InsertingCremOrders.push(item);
            }
        });

        ReadyPlaqueOrders.forEach(function(item) {
            if (!(item[key] in days)) {
                days[item[key]] = [];
            }
            let tempDays = days[item[key]];
            if (!tempDays.ReadyPlaqueOrders) {
                tempDays.ReadyPlaqueOrders = [];
                tempDays.ReadyPlaqueOrders.push(item);
            } else {
                tempDays.ReadyPlaqueOrders.push(item);
            }
        });

        return days;
    }

    renderInsertionTable = (listCremations, hideCompleted) => {
        const filteredList = listCremations.filter(item => !hideCompleted || !(item.CremationComplete));
        if (!filteredList.length) return null;
        return (
            <Fragment>
                <h3 className="page-break--no-orphan">
                    INSERTION ACTIONS
                </h3>
                {filteredList.map((item, idx) => {
                    const varStat = {
                        variant: !!item.CremationComplete || !item.ReadyForCremation
                            ? 'relaxed'
                            : 'priority',
                        status: !!item.CremationComplete
                            ? 'COMPLETED'
                            : item.CremationNumber > 0
                                ? 'STARTED'
                                : !item.DeliveryDate
                                    ? 'PENDING'
                                    : !item.ReadyForCremation
                                        ? 'NOT READY'
                                        : 'READY'
                    };
                    return this.renderCremationRunSheetItem(item, varStat.variant, 'INSERTION ' + varStat.status, FireIcon, 1 + idx);
                })}
            </Fragment>
        );
    };

    renderArrivalTable = (listCremations, hideCompleted) => {
        const filteredList = listCremations.filter(item => !hideCompleted || !(item.DeliveryDate || item.CremationNumber > 0));
        if (!filteredList.length) return null;
        return (
            <Fragment>
                <h3 className="page-break--no-orphan">
                    ARRIVAL ACTIONS
                </h3>
                {filteredList.map((item, idx) => {
                    const varStat = {
                        variant: !!item.CremationComplete
                            ? 'relaxed'
                            : !item.DeliveryDate || item.CremationNumber > 0
                                ? 'priority'
                                : 'relaxed',
                        status: !!item.CremationComplete
                            ? 'COMPLETED'
                            : item.CremationNumber > 0
                                ? 'INSERTED'
                                : !item.DeliveryDate
                                    ? 'PENDING'
                                    : !item.ReadyForCremation
                                        ? 'NOT READY'
                                        : 'READY'
                    };
                    return this.renderCremationRunSheetItem(item, varStat.variant, 'ARRIVAL ' + varStat.status, DriverArrivalsIcon, 1 + idx);
                })}
            </Fragment>
        );
    };

    renderCremationRunSheetItem = (cremation, variant, status, icon, countA) => {
        return (
            <CremationRunsheet
                key={'cr' + cremation.ID}
                variant={variant}
                runsheetStatus={status}
                runsheetType={icon}
                cremation={cremation}
                countA={countA}
            />
        );
    };

    renderPlaqueTable = (listPlaques, hideCompleted) => {
        const filteredList = listPlaques.filter(item => !hideCompleted || !('Complete' === item.Status));
        if (!filteredList.length) return null;
        return (
            <Fragment>
                <h3 className="page-break--no-orphan">
                    INSTALLATION ACTIONS
                </h3>
                {filteredList.map(item => {
                    const varStat = {
                        variant: ['Overdue', 'Urgent'].includes(item.Status) ? 'priority' : '',
                        status: ['Overdue', 'Urgent'].includes(item.Status)
                            ? 'URGENT'
                            : 'Complete' === item.Status
                                ? 'COMPLETED'
                                : 'READY'
                    };
                    return this.renderPlaqueRunSheetItem(item, varStat.variant, 'INSTALLATION ' + varStat.status);
                })}
            </Fragment>
        );
    };

    renderPlaqueRunSheetItem = (plaque, variant, status) => {
        return (
            <PlaqueRunsheet
                key={'pl' + plaque.ID}
                variant={variant}
                runsheetStatus={status}
                runsheetType={PlaqueSignIcon}
                plaque={plaque}
            />
        );
    };

    restartPolling = () => {
        if (this.storePolling) {
            this.storePolling.stop.forEach(obj => obj());
            this.storePolling.start.forEach(obj => obj(GQL_REFETCH_60S));
        }
    };

    updateSession = ({ newDates, newPreferences }) => {
        const saved = JSON.parse(sessionStorage.getItem('runsheet')) || {};
        if (newDates) {
            saved.date = moment(newDates.dateFrom).format('YYYYMMDD');
            saved.until = moment(newDates.dateTo).format('YYYYMMDD');
        }
        if (newPreferences) {
            Object.keys(newPreferences).forEach(key => saved[key] = newPreferences[key]);
        }
        sessionStorage.setItem('runsheet', JSON.stringify(saved));
        this.forceUpdate();
    };
}

const cremationsQuery = gql`
    ${DispatchDate}
    ${SpecialInstruction}
    ${ReflectionRoom}
    ${Deceased}
    ${Member}

    fragment CremationRunsheetFragment on CremationOrder {
        ID
        LegacyKey
        EstimatedDeceasedWeight
        DeceasedWeight
        CremationNumber
        CremationActionNotes
        CremationPending
        CremationComplete
        IsDeliveryOnly
        FuneralService
        SameDayCremation
        NextDayCremation
        CremationDate
        CremationTime
        DeliveryDate
        DeliveryTime
        EstimatedDeliveryDate
        EstimatedDeliveryTime
        EstimatedDeliveredBy
        EstimatedInsertionDate
        EstimatedInsertionTime
        InsertionDate
        InsertionTime
        CremationEndDate
        CremationEndTime
        ReadyForCremation
        HasMissingCertificates
        AdditionalNotes
        HadPacemaker
        HadPacemakerRemoved
        DispatchDates {
            ...DispatchDate
        }
        SpecialInstructions {
            ...SpecialInstruction
        }
        WitnessOfInsertionRequired
        ReflectionRoomRequired
        ReflectionRoom {
            ...ReflectionRoom
        }
        Deceased {
            ...Deceased
        }
        CremationOperator {
            ...Member
        }
        DeliveryReceivedBy {
            ...Member
        }
        Funeral {
            ID
            PlaceOfService {
                ID
                Type
            }
        }
    }

    query runsheetCremations($fromDate: String, $toDate: String, $filterBy:[Filters]) {
        # isHeldOver:true searches insertion dates & includes finalised
        insertingCremOrders: readCremationOrders(isHeldOver: true, fromDate: $fromDate, toDate: $toDate, filterBy: $filterBy) {
            edges {
                node {
                    ...CremationRunsheetFragment
                }
            }
        }
        # isHeldOver:false searches arrival dates & includes finalised
        arrivingCremOrders: readCremationOrders(isHeldOver: false, fromDate: $fromDate, toDate: $toDate, filterBy: $filterBy) {
            edges {
                node {
                    ...CremationRunsheetFragment
                }
            }
        }
    }
`;
const plaquesQuery = gql`
    ${Deceased}
    fragment PlaqueRunsheetFragment on PlaqueOrder {
        ID
        LegacyKey
        Status
        InstallationDate
        InstallationTime
        FamilyAttendance
        FamilyContact
        FamilyAttendanceDate
        FamilyAttendanceTime
        PlacementByCouncil
        PlaquePosition
        PlaqueMaterial
        AttachmentMethod
        WallPosition
        QuoteSignedDate
        Deceased {
            ...Deceased
        }
        Ashes {
            ID
            Ashes
            ContainmentStyle
            Comment
        }
        PlaqueCombination {
            ID
            LocationType
        }
        PlaqueLocation {
            ID
            Name
            AddressLine1
            AddressLine2
            State
            Suburb
            Postcode
        }
    }

    query runsheetPlaques($fromDate: String, $toDate: String) {
        readyPlaqueOrders: readPlaqueOrders(fromDate: $fromDate, toDate: $toDate) {
            edges {
                node {
                    ...PlaqueRunsheetFragment
                }
            }
        }
    }
`;

export default withErrorBoundary(compose(withRouter, withStyles({}))(Runsheet), { fallback: ErrorFallback });
